import { useState, useEffect } from 'react'
import SubHeaders from '@/components/Headers/SubHeaders'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { getProcess, updateProcessStatus, deleteProcess } from '../../services'
import { paths } from '@/utils/path'
import { useParams, useNavigate } from 'react-router-dom'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useTourHandler } from '@/contexts/tour-handler'
import { useProcessDetailTour } from '../../hooks/use-process-detail-tour'
import { GridInformation, Tabs, Button } from 'vnetwork-ui'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import useTabs from '@/hooks/useTabs'
import { tabs } from './tabs'
import ApprovalActions from './ApprovalActions'
import { Pencil, Send, XCircle, Trash2 } from 'lucide-react'
import { Action } from '@/components/ButtonGroupActions'

const ProcessDetail = () => {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false)
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
    const [cancelReason, setCancelReason] = useState('')
    const [deleteReason, setDeleteReason] = useState('')

    const { data: process, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.WAREHOUSE_PROCESSES, id],
        queryFn: () => getProcess(id!),
    })

    // Tour guide integration - pass process status to filter relevant steps
    const { startTour } = useProcessDetailTour({
        status: process?.status as any,
    })
    const { registerHandler, unregisterHandler } = useTourHandler()

    // Register tour handler for keyboard shortcut (Ctrl+Shift+H)
    useEffect(() => {
        registerHandler(startTour)
        return () => unregisterHandler()
    }, [registerHandler, unregisterHandler, startTour])

    // Status update mutation (submit/cancel)
    const { mutate: updateStatus, isPending: isUpdating } =
        useMutationWithHandler(
            {
                mutationFn: (data: { status: string; reason?: string }) =>
                    updateProcessStatus(id!, data as any),
                onSuccess: () => {
                    setCancelReason('')
                    setCancelDialogOpen(false)
                },
            },
            {
                successMessage: 'Process status updated',
                errorMessage: 'Failed to update status',
                reactQueryKeys: QUERY_KEYS.WAREHOUSE_PROCESSES,
            }
        )

    // Delete mutation
    const { mutate: remove, isPending: isDeleting } = useMutationWithHandler(
        {
            mutationFn: () =>
                deleteProcess(
                    id!,
                    deleteReason ? { reason: deleteReason } : undefined
                ),
            onSuccess: () => {
                setDeleteDialogOpen(false)
                navigate(paths.warehouseProcesses)
            },
        },
        {
            successMessage: 'Process deleted',
            errorMessage: 'Failed to delete process',
            reactQueryKeys: QUERY_KEYS.WAREHOUSE_PROCESSES,
        }
    )

    const { TabComponent, TabProps } = useTabs({ tabs: tabs(process) })

    // Helper to get process type badge variant
    const getProcessTypeBadgeVariant = (type: string) => {
        switch (type) {
            case 'import':
                return 'default'
            case 'export_loan':
                return 'secondary'
            case 'export_sale':
                return 'destructive'
            case 'transfer':
                return 'outline'
            default:
                return 'default'
        }
    }

    // Helper to get status badge variant
    const getStatusBadgeVariant = (status: string) => {
        switch (status) {
            case 'completed':
                return 'default'
            case 'approved':
                return 'default'
            case 'pending':
                return 'secondary'
            case 'cancelled':
                return 'destructive'
            default:
                return 'outline'
        }
    }

    // Helper to format process type
    const formatProcessType = (type: string) => {
        switch (type) {
            case 'export_loan':
                return 'Export (Loan)'
            case 'export_sale':
                return 'Export (Sale)'
            case 'import':
                return 'Import'
            case 'transfer':
                return 'Transfer'
            default:
                return type
        }
    }

    // Helper to format date
    const formatDateTime = (dateStr?: string) => {
        if (!dateStr) return 'N/A'
        return new Date(dateStr).toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        })
    }

    // Status checks for action visibility
    const isDraft = process?.status === 'draft'
    const isPending = process?.status === 'pending_approval'
    const isCompleted = process?.status === 'completed'

    const canEdit = isDraft || isPending
    const canSubmit = isDraft
    const canCancel = isDraft || isPending
    const canDelete = !isCompleted

    // Build actions for SubHeaders
    const actions: Action[] = []

    // Submit for Approval - only for draft
    if (canSubmit) {
        actions.push({
            label: 'Submit',
            icon: <Send size={16} />,
            onClick: () => updateStatus({ status: 'pending_approval' }),
            variant: 'contained',
            disabled: isUpdating,
        })
    }

    // Edit - for draft/pending
    if (canEdit) {
        actions.push({
            label: 'Edit',
            icon: <Pencil size={16} />,
            onClick: () => navigate(`/warehouse/processes/${id}/edit`),
            variant: 'outlined',
        })
    }

    // Cancel - for draft/pending
    if (canCancel) {
        actions.push({
            label: 'Cancel',
            icon: <XCircle size={16} />,
            onClick: () => setCancelDialogOpen(true),
            variant: 'outlined',
            color: 'error',
            disabled: isUpdating,
        })
    }

    // Delete - any non-completed
    if (canDelete) {
        actions.push({
            label: 'Delete',
            icon: <Trash2 size={16} />,
            onClick: () => setDeleteDialogOpen(true),
            variant: 'outlined',
            color: 'error',
            disabled: isDeleting,
        })
    }

    // Render loading/error state at the end (after all hooks)
    if (renderState()) return renderState()

    return (
        <div data-tour="process-detail-page">
            <div data-tour="process-detail-header">
                <SubHeaders
                    actions={actions}
                    breadcrumbs={[
                        {
                            path: paths.warehouse,
                            title: 'Capacities & Resources',
                        },
                        {
                            path: paths.warehouseProcesses,
                            title: 'Processes',
                        },
                        {
                            path: '',
                            title: process?.code || '',
                        },
                    ]}
                />
            </div>
            <GridInformation
                data-tour="process-detail-info"
                data={[
                    {
                        label: 'Process Code',
                        value: process?.code || '-',
                    },
                    {
                        label: 'Type',
                        value: (
                            <Badge
                                variant={getProcessTypeBadgeVariant(
                                    process?.type || ''
                                )}
                            >
                                {formatProcessType(process?.type || '')}
                            </Badge>
                        ),
                    },
                    {
                        label: 'Status',
                        value: (
                            <Badge
                                variant={getStatusBadgeVariant(
                                    process?.status || ''
                                )}
                            >
                                {process?.status?.toUpperCase() || ''}
                            </Badge>
                        ),
                    },
                    {
                        label: 'From Location',
                        value: process?.source?.name || 'N/A',
                    },
                    {
                        label: 'To Location',
                        value: process?.destination?.name || 'N/A',
                    },
                    {
                        label: 'From Address',
                        value: process?.from_location?.address || '-',
                        hidden: !process?.from_location?.address,
                    },
                    {
                        label: 'To Address',
                        value: process?.to_location?.address || '-',
                        hidden: !process?.to_location?.address,
                    },
                    {
                        label: 'Supplier',
                        value: process?.supplier?.name || '-',
                        hidden: !process?.supplier,
                    },
                    {
                        label: 'Supplier Phone',
                        value: process?.supplier?.phone || '-',
                        hidden: !process?.supplier?.phone,
                    },
                    {
                        label: 'Supplier Email',
                        value: process?.supplier?.email || '-',
                        hidden: !process?.supplier?.email,
                    },
                    {
                        label: 'Reason',
                        value: process?.reason || '-',
                        className: 'lg:col-span-2',
                    },
                    {
                        label: 'Notes',
                        value: process?.notes || '-',
                        className: 'lg:col-span-2',
                        hidden: !process?.notes,
                    },
                    {
                        label: 'Tech Lead Approval',
                        value: process?.tech_lead_approval_status ? (
                            <Badge
                                variant={getStatusBadgeVariant(
                                    process.tech_lead_approval_status
                                )}
                            >
                                {process.tech_lead_approval_status.toUpperCase()}
                            </Badge>
                        ) : (
                            '-'
                        ),
                        hidden: !process?.tech_lead_approval_status,
                    },
                    {
                        label: 'Tech Lead Approved At',
                        value: formatDateTime(process?.tech_lead_approved_at),
                        hidden: !process?.tech_lead_approved_at,
                    },
                    {
                        label: 'Tech Lead Notes',
                        value: process?.tech_lead_notes || '-',
                        className: 'lg:col-span-2',
                        hidden: !process?.tech_lead_notes,
                    },
                    {
                        label: 'Warehouse Keeper ID',
                        value: process?.warehouse_keeper_id || '-',
                        hidden: !process?.warehouse_keeper_id,
                    },
                    {
                        label: 'Warehouse Approved At',
                        value: formatDateTime(process?.warehouse_approved_at),
                        hidden: !process?.warehouse_approved_at,
                    },
                    {
                        label: 'Warehouse Notes',
                        value: process?.warehouse_notes || '-',
                        className: 'lg:col-span-2',
                        hidden: !process?.warehouse_notes,
                    },
                    {
                        label: 'Created At',
                        value: formatDateTime(process?.created_at),
                    },
                    {
                        label: 'Updated At',
                        value: formatDateTime(process?.updated_at),
                    },
                ]}
                head={{
                    title: 'Process Information',
                }}
            />

            {/* Approval Actions - only shown when process is pending_approval */}
            <div data-tour="process-approval-actions">
                {process && <ApprovalActions process={process} />}
            </div>

            <div data-tour="process-tabs">
                <Tabs {...TabProps} />
            </div>

            <div data-tour="process-tab-content">{TabComponent}</div>

            {/* Cancel confirmation dialog */}
            <Dialog
                open={cancelDialogOpen}
                onOpenChange={(open) => {
                    setCancelDialogOpen(open)
                    if (!open) setCancelReason('')
                }}
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Cancel Process</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to cancel this process? This
                            action cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2 py-4">
                        <Label>Reason (Optional)</Label>
                        <Textarea
                            value={cancelReason}
                            onChange={(e) => setCancelReason(e.target.value)}
                            placeholder="Enter reason for cancellation..."
                            rows={3}
                        />
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outlined"
                            onClick={() => setCancelDialogOpen(false)}
                            disabled={isUpdating}
                        >
                            Keep Process
                        </Button>
                        <Button
                            variant="contained"
                            color="warning"
                            onClick={() =>
                                updateStatus({
                                    status: 'cancelled',
                                    reason: cancelReason || undefined,
                                })
                            }
                            disabled={isUpdating}
                        >
                            Cancel Process
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete confirmation dialog */}
            <Dialog
                open={deleteDialogOpen}
                onOpenChange={(open) => {
                    setDeleteDialogOpen(open)
                    if (!open) setDeleteReason('')
                }}
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Delete Process</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to delete this process? This
                            will permanently remove it from the system.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2 py-4">
                        <Label>Reason (Optional)</Label>
                        <Textarea
                            value={deleteReason}
                            onChange={(e) => setDeleteReason(e.target.value)}
                            placeholder="Enter reason for deletion..."
                            rows={3}
                        />
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outlined"
                            onClick={() => setDeleteDialogOpen(false)}
                            disabled={isDeleting}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={() => remove()}
                            disabled={isDeleting}
                        >
                            Delete Process
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}

export default ProcessDetail
